import {
  ComponentProps,
  ReactElement,
  cloneElement,
  createContext,
  useContext,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useState,
  useCallback,
  useEffect,
  RefObject,
  useRef,
} from 'react'

import { createPortal } from 'react-dom'
import joinClassNames from 'utilities/joinClassNames'

import styles from './styles.module.scss'
import Text from 'components/Text'

interface BottomSheetContextProps {
  isOpen: boolean
  isClosing: boolean
  open: () => void
  close: () => void
  triggerRef: RefObject<HTMLButtonElement>
  contentRef: RefObject<HTMLDivElement>
}

const BottomSheetContext = createContext<BottomSheetContextProps | null>(null)

const useBottomSheetContext = () => {
  const ctx = useContext(BottomSheetContext)
  if (!ctx)
    throw new Error(
      'useBottomSheetContext must be used inside BottomSheet Root',
    )
  return ctx
}

interface RootProps extends ComponentProps<'div'> {
  onClose?: () => void
}

const Root = ({ children, className, onClose, ...props }: RootProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const open = useCallback(() => {
    setIsClosing(false)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => setIsOpen(false), 250)
    onClose?.()
  }, [onClose])

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (
        isOpen &&
        contentRef.current &&
        !contentRef.current.contains(ev.target as Node) &&
        !triggerRef.current?.contains(ev.target as Node)
      ) {
        close()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, close])

  useEffect(() => {
    if (triggerRef.current) {
      triggerRef.current.setAttribute('data-active', isOpen ? 'true' : 'false')
    }
  }, [isOpen])

  return (
    <BottomSheetContext.Provider
      value={{
        isOpen,
        isClosing,
        open,
        close,
        triggerRef,
        contentRef,
      }}
    >
      <div {...props} className={joinClassNames(styles.root, className)}>
        {children}
      </div>
    </BottomSheetContext.Provider>
  )
}

const Title = ({ className, children, ...props }: ComponentProps<'h2'>) => {
  const { close } = useBottomSheetContext()

  const startY = useRef<number | null>(null)
  const currentY = useRef<number | null>(null)
  const elementRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: ReactTouchEvent) => {
    startY.current = e.touches[0].clientY
  }, [])

  const handleTouchMove = useCallback(
    (e: ReactTouchEvent) => {
      currentY.current = e.touches[0].clientY
    },
    [elementRef],
  )

  const handleTouchEnd = useCallback(() => {
    const diff = (currentY.current ?? 0) - (startY.current ?? 0)
    if (diff > 100) {
      close()
    } else if (elementRef.current) {
      elementRef.current.style.transform = 'translateY(0)'
      elementRef.current.style.transition = 'transform 0.25s ease'
      setTimeout(() => {
        if (elementRef.current) elementRef.current.style.transition = ''
      }, 250)
    }
    startY.current = null
    currentY.current = null
  }, [close, elementRef])

  return (
    <div
      ref={elementRef}
      className={styles.header}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      {...props}
    >
      <div className={styles.handle} />

      <Text
        as="h2"
        size="lg"
        weight="semibold"
        className={joinClassNames(styles.title, className)}
        {...props}
        color="element-default"
      >
        {children}
      </Text>
    </div>
  )
}

const Trigger = ({
  children,
  onClick,
  disabled,
  ...props
}: ComponentProps<'button'>) => {
  const { open, triggerRef, isOpen } = useBottomSheetContext()

  const handleClick = (ev: ReactMouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(ev)
    } else {
      ev.stopPropagation()
    }

    open()
  }

  return cloneElement(children as ReactElement, {
    onClick: !disabled ? handleClick : undefined,
    ref: triggerRef,
    'data-active': isOpen,
    ...props,
  })
}

const Content = ({ className, children, ...props }: ComponentProps<'div'>) => {
  const { isOpen, isClosing, contentRef, close } = useBottomSheetContext()

  const handleOverlayMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) close()
    },
    [close],
  )

  if (!isOpen) return null

  return createPortal(
    <div className={styles.overlay} onMouseDown={handleOverlayMouseDown}>
      <div
        ref={contentRef}
        className={joinClassNames(
          styles.container,
          isClosing ? styles.closing : styles.opening,
        )}
      >
        <div className={joinClassNames(styles.content, className)} {...props}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface Option extends ComponentProps<'button'> {
  selected?: boolean
}

const Option = ({
  children,
  onClick,
  className,
  selected = false,
  ...props
}: Option) => {
  const { close } = useBottomSheetContext()

  const handleClick = useCallback(
    (ev: ReactMouseEvent<HTMLButtonElement>) => {
      ev.stopPropagation()
      onClick?.(ev)
      close()
    },
    [onClick, close],
  )

  return (
    <button
      {...props}
      onClick={handleClick}
      className={joinClassNames(styles.option, className)}
    >
      {children}
      {selected && <div className={styles.indicator} />}
    </button>
  )
}

const BottomSheet = {
  Root,
  Title,
  Trigger,
  Content,
  Option,
}

export default BottomSheet
